/**
 * "DaVinci is not available locally."
 *
 * FRONTEND_DESIGN §6.4 requires this sentence by name, and issue #5's
 * definition of done requires that it "renders without the API, and explains
 * what to do rather than showing a spinner forever".
 *
 * Both halves matter. A spinner is a claim that waiting will help; when the
 * server process is not running, waiting will never help, and the founder is
 * the only person who can start it. So this state says what happened, says
 * what to do about it, and offers a retry — and it is a plain component with
 * no data dependency, so it can render when nothing else can.
 *
 * It also never says or implies that an answer was wrong. §6.4: "do not label
 * the answer wrong." A network fault is not evidence about the learner, and a
 * UI that blurs the two manufactures exactly the kind of unearned conclusion
 * this project exists to avoid.
 */

import { useMessages } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';

const messages = defineMessages({
  en: {
    title: 'DaVinci is not available locally',
    body: 'The DaVinci server is not answering on this machine. Nothing has been lost — your data is on this computer, and this page simply cannot reach it right now.',
    step1: 'Check that the DaVinci server is still running.',
    step2: 'If it is not, start it again.',
    step3: 'Then retry. You may be asked to sign in again — a restart ends every session.',
    retry: 'Retry',
  },
  he: {
    title: 'DaVinci אינו זמין במחשב הזה',
    body: 'שרת DaVinci אינו עונה במחשב הזה. שום דבר לא אבד — הנתונים שלך נמצאים במחשב הזה, והדף פשוט לא מצליח להגיע אליהם כרגע.',
    step1: 'בדוק ששרת DaVinci עדיין פועל.',
    step2: 'אם הוא לא פועל, הפעל אותו מחדש.',
    step3: 'אחר כך נסה שוב. ייתכן שתתבקש להתחבר מחדש — הפעלה מחדש של השרת מסיימת כל חיבור.',
    retry: 'נסה שוב',
  },
});

export interface LocalUnavailableProps {
  /** Retries the failed query. Omitted where there is nothing to retry. */
  readonly onRetry?: () => void;
  /**
   * A short technical detail for the founder, who is also the developer.
   * Never a server-supplied message — §12 fixes those server-side so no path
   * or stack can travel in one, and echoing them would move that
   * responsibility across the boundary.
   */
  readonly detail?: string;
}

export function LocalUnavailable({ onRetry, detail }: LocalUnavailableProps): React.JSX.Element {
  const t = useMessages(messages);
  return (
    // `role="alert"` rather than a live region added later: this replaces the
    // page content, and a screen-reader user who navigated here needs to be
    // told why the thing they asked for is not present.
    <div className="notice notice-screen" role="alert">
      <h2>{t.title}</h2>
      <p>{t.body}</p>
      <ol className="notice-steps">
        <li>{t.step1}</li>
        <li>{t.step2}</li>
        <li>{t.step3}</li>
      </ol>
      {onRetry !== undefined ? (
        <button type="button" onClick={onRetry}>
          {t.retry}
        </button>
      ) : null}
      {detail !== undefined ? <p className="notice-detail">{detail}</p> : null}
    </div>
  );
}
